import { datadogRum } from '@datadog/browser-rum'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { getCkoCreditCardDialogMountId, getCkoStoredCardDialogMountId } from '@payments/lib/checkout-pay/ckoMountTarget'
import { useEffect, useRef, useState } from 'react'
import { PaymentCancelledError } from '../core/checkoutPayFlowController'
import { isCkoMountContainerDetachedError } from '../core/cko-mount-container-detached-error'
import { type CkoMount } from '../core/paymentService'
import { type CreditCardDialogContext, type CreditCardDialogFlowActions } from './credit-card-dialog-types'

/** 掛載逾時上限：mounting 期間無法取消／關閉 dialog，逾時拋錯是使用者唯一的離開途徑。 */
const MOUNT_TIMEOUT_MS = 30_000

/** 卡片欄位未出現的等待上限，逾時改為可關閉的錯誤。 */
const SDK_CONTENT_TIMEOUT_MS = 10_000

export interface UseCreditCardDialogParams extends CreditCardDialogFlowActions {
  context: CreditCardDialogContext
  /** mount 或付款失敗時呼叫，消費線負責關閉 dialog 並呈現錯誤。 */
  onError: (error: unknown) => void
  /** 值變動時觸發重新掛載，用於容器 id 不變但需要重掛的場景（如 ResponsiveDialog 斷點重建子樹）。 */
  remountKey?: unknown
}

export const useCreditCardDialog = (props: UseCreditCardDialogParams) => {
  const { context, mountCreditCardFields, submitAndExecute, cancelInFlight, onError, remountKey } = props
  const [isMounting, setIsMounting] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  /** submit（含 3DS）完成、進入 execution 後為 true，供 UI 顯示整頁 loading。 */
  const [hasError, setHasError] = useState<boolean>(false)
  const [isSdkReady, setIsSdkReady] = useState<boolean>(false)
  /** 使用者按「重新再試」時 +1，觸發掛載 effect 重跑（重新 init + 重掛 SDK）。 */
  const [retryNonce, setRetryNonce] = useState<number>(0)
  const containerId = getCkoCreditCardDialogMountId(context.paymentTransactionId)
  const storedCardContainerId = getCkoStoredCardDialogMountId(context.paymentTransactionId)
  // in-flight guard，防付款鈕被重複點擊觸發多次 submit
  const isSubmittingRef = useRef(false)
  const activeMountRef = useRef<CkoMount | null>(null)
  /** React StrictMode 雙重 effect guard：同 session 觸發兩次時，cleanup 靠此確認無接班輪次再卸載。 */
  const mountRunRef = useRef(0)

  const reportFailure = useCallbackEvent((failure: unknown, functionName: string) => {
    datadogRum.addError(failure instanceof Error ? failure : new Error(String(failure)), {
      function: functionName,
      paymentTransactionId: context.paymentTransactionId,
      orderHash: context.orderHash,
    })
    onError(failure)
  })

  /** submit 進行中拒絕取消，避免卡片已扣款但 execution 沒執行、訂單卡在待付款。 */
  const cancel = useCallbackEvent((reason: string): boolean => {
    if (isSubmittingRef.current) return false
    cancelInFlight(reason)
    return true
  })

  /** 以固定身分包裝，避免消費端每次 render 提供新的參照觸發 effect 重跑，導致卸載並清除使用者已輸入的卡號。 */
  const mountFields = useCallbackEvent(() => mountCreditCardFields(context, containerId, storedCardContainerId))

  // 重掛條件：容器 id（由 paymentTransactionId 推導，換一筆交易才變）或 remountKey；其餘 re-render 不重掛。
  useEffect(function mountCreditCardOnDialogOpen() {
    let isActive = true
    const myRunId = ++mountRunRef.current
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    setIsMounting(true)
    setHasError(false)

    const start = async () => {
      // submit 進行中不重掛：applyCkoSession 開頭的 unmount() 會拆掉回報授權的 component，繞過 cleanup 的 submit guard。
      if (isSubmittingRef.current) {
        setIsMounting(false)
        return
      }
      const mountPromise = mountFields()
      try {
        const ckoMount = await Promise.race([
          mountPromise,
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('[useCreditCardDialog] mount timed out')),
              MOUNT_TIMEOUT_MS,
            )
          }),
        ])
        // 本輪 effect 已作廢（dialog 關閉或 dep 變動）：掛載無人使用，就地卸載，否則殘留在
        // singleton module 上，下次開啟時容器已是新 node，畫面空白。
        // 例外：接班輪次已接手（mountRunRef 遞增）時跳過 — React StrictMode 雙重 effect 下，
        // 卸載會拆掉接班人剛掛好的元件，dialog 重開時空白。
        if (!isActive) {
          if (mountRunRef.current === myRunId) ckoMount.unmount()
          return
        }
        activeMountRef.current = ckoMount
      }
      catch (failure) {
        // Promise.race 不中止落敗的 mountFields()：逾時後才 resolve 的掛載無持有者，就地卸載
        mountPromise.then(lateMount => lateMount.unmount(), () => undefined)
        if (!isActive) return
        /** 可重試狀態：保持靜默等待重新掛載，不視為付款失敗、不關窗。 */
        if (isCkoMountContainerDetachedError(failure)) return
        // 設 hasError 鎖住付款鈕：欄位未掛載完成，放行會把 submit 送往未掛載的 module。
        setHasError(true)
        if (failure instanceof PaymentCancelledError) return
        reportFailure(failure, 'useCreditCardDialog.mountCreditCardOnDialogOpen')
      }
      finally {
        clearTimeout(timeoutId)
        if (isActive) setIsMounting(false)
      }
    }
    start()

    return () => {
      isActive = false
      // submit 進行中不卸載（同 cancel() 規則）：卸載會拆掉回報授權的 component，execution 不執行。
      // 殘留掛載由下一次 applyCkoSession 開頭的 unmount() 清除。
      if (isSubmittingRef.current) return
      activeMountRef.current?.unmount()
      activeMountRef.current = null
    }
  }, [containerId, storedCardContainerId, mountFields, reportFailure, remountKey, retryNonce])

  // 獨立於 mount effect：合併後任一 dep 參照變動都會在 dialog 仍開啟時觸發 cancel，
  // 而 cancel 走 PaymentCancelledError 分支被靜默忽略。用參照固定的 cancel（非消費端的 cancelInFlight）
  // 當 dep，避免消費端每次 render 的新函式參照觸發 cleanup。
  useEffect(function cancelOnDialogUnmount() {
    return () => {
      cancel('dialog unmounting')
    }
  }, [cancel])

  /**
   * Flatsome 主題 button !important 樣式滲入 CKO SDK 容器；CSS override 同層級無法覆蓋，
   * 改用 style.setProperty(..., 'important')，inline !important 優先級最高。
   */
  useEffect(function resetFlatsomeButtonStylesInCkoContainer() {
    const FLATSOME_BUTTON_OVERRIDES: [string, string][] = [
      ['min-height', 'revert'],
      ['margin-bottom', 'revert'],
    ]

    const applyReset = (root: HTMLElement) => {
      root.querySelectorAll<HTMLButtonElement>('button').forEach((button) => {
        FLATSOME_BUTTON_OVERRIDES.forEach(([property, value]) => {
          button.style.setProperty(property, value, 'important')
        })
      })
    }

    const targets = [document.getElementById(containerId), document.getElementById(storedCardContainerId)]
      .filter((element): element is HTMLElement => element !== null)
    const observer = new MutationObserver(() => targets.forEach(applyReset))
    targets.forEach((element) => {
      observer.observe(element, { childList: true, subtree: true })
      applyReset(element)
    })

    return () => observer.disconnect()
  }, [containerId, storedCardContainerId, remountKey])

  /**
   * 隱藏 CKO 內建的跨商家「記住卡片」，本站用自己的儲卡機制。
   * 先取消勾選再隱藏，否則 SDK 會誤判欄位未填、送出報錯；不可用 CSS 強制隱藏，會讓 SDK 崩潰。
   */
  useEffect(function hideCkoNativeRememberMe() {
    const hideRememberMe = (root: HTMLElement) => {
      root.querySelectorAll<HTMLElement>('[id^="remember-me-"]').forEach((rememberMeContainer) => {
        const checkbox = rememberMeContainer.querySelector<HTMLInputElement>('[name="shouldSaveCardAtRememberMe"]')
        if (checkbox?.checked) {
          checkbox.checked = false
          checkbox.dispatchEvent(new Event('change', { bubbles: true }))
        }
        rememberMeContainer.style.display = 'none'
      })
    }

    const targets = [document.getElementById(containerId), document.getElementById(storedCardContainerId)]
      .filter((element): element is HTMLElement => element !== null)
    const observer = new MutationObserver(() => targets.forEach(hideRememberMe))
    targets.forEach((element) => {
      observer.observe(element, { childList: true, subtree: true })
      hideRememberMe(element)
    })

    return () => observer.disconnect()
  }, [containerId, storedCardContainerId, remountKey])

  // isSdkReady 綁「容器是否已有子節點」而非掛載生命週期旗標：SDK 塞入內容的當下同步切掉 skeleton，兩者才不會並存造成抖動。
  useEffect(function trackSdkContent() {
    const cardElement = document.getElementById(containerId)
    const storedCardElement = document.getElementById(storedCardContainerId)
    const syncReady = () => setIsSdkReady((cardElement?.hasChildNodes() ?? false) || (storedCardElement?.hasChildNodes() ?? false))
    const observer = new MutationObserver(syncReady)
    ;[cardElement, storedCardElement].forEach(element => element && observer.observe(element, { childList: true }))
    syncReady()
    return () => observer.disconnect()
  }, [containerId, storedCardContainerId, remountKey])

  /**
   * 卡片欄位遲遲沒出現時逾時轉為可關閉的錯誤，讓使用者能關窗改走待付款頁重試，不會卡在載入。
   * 送出後排除：3DS 期間 SDK 可能清空容器再注入挑戰 iframe，isSdkReady 短暫翻 false 不代表掛載失敗。
   */
  useEffect(function failIfSdkContentNeverAppears() {
    if (isMounting || isSdkReady || hasError || isSubmitting) return
    const timeoutId = setTimeout(() => {
      datadogRum.addError(new Error('[useCreditCardDialog] SDK content did not appear after mount resolved'), {
        function: 'useCreditCardDialog.failIfSdkContentNeverAppears',
        paymentTransactionId: context.paymentTransactionId,
        orderHash: context.orderHash,
      })
      setHasError(true)
    }, SDK_CONTENT_TIMEOUT_MS)
    return () => clearTimeout(timeoutId)
  }, [isMounting, isSdkReady, hasError, isSubmitting, context.paymentTransactionId, context.orderHash])

  const submit = async () => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      // 成功時 submitAndExecute 內部已呼叫 window.location.href 導頁；刻意不 reset loading / guard，
      // 讓付款鈕鎖到頁面真正卸載為止，避免導頁未完成的空窗內鈕變回可按、被重複送出。
      await submitAndExecute(context)
    }
    catch (failure) {
      isSubmittingRef.current = false
      setIsSubmitting(false)
      if (failure instanceof PaymentCancelledError) return
      reportFailure(failure, 'useCreditCardDialog.submit')
    }
  }

  /** 「重新再試」：清錯誤並重跑掛載 effect，重新 init + 重掛 SDK。 */
  const retry = useCallbackEvent(() => {
    setHasError(false)
    setRetryNonce(nonce => nonce + 1)
  })

  return {
    isMounting,
    isSubmitting,
    hasError,
    isSdkReady,
    containerId,
    storedCardContainerId,
    submit,
    cancel,
    retry,
  }
}
